import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { type ActivatedRouteSnapshot, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { LoadingBarStore } from '@sds/loading-bar';
import { DataLayerService } from '@sds/shared/services';
import { EMPTY, filter, fromEvent, map, type Observable, race, switchMap, take, takeUntil, tap, timer } from 'rxjs';

/** A request starting within this window after the last one finished still belongs to the same page load. */
export const ROUTE_READY_QUIET_MS = 500;
export const ROUTE_READY_TIMEOUT_MS = 60_000;

export interface RouteReadyMeasurement {
  route: string;
  durationMs: number;
  firstLoad: boolean;
  timedOut: boolean;
}

/**
 * Measures "time to data": from the start of a navigation until its HTTP requests have finished,
 * and pushes it to the dataLayer as a `routeReady` event.
 */
@Injectable({
  providedIn: 'root'
})
export class RouteTimingService {
  private readonly router = inject(Router);
  private readonly loadingBarStore = inject(LoadingBarStore);
  private readonly dataLayer = inject(DataLayerService);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly requestsPending$ = toObservable(this.loadingBarStore.requestsPending);

  private started = false;
  private firstNavigationDone = false;

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        // the first load is timed from the document's navigation start (0), so it includes app bootstrap
        map(() => ({ startedAt: this.firstNavigationDone ? this.now() : 0, firstLoad: !this.firstNavigationDone })),
        switchMap(({ startedAt, firstLoad }) =>
          this.document.visibilityState === 'hidden' ? EMPTY : this.measure(startedAt, firstLoad)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(measurement => {
        this.dataLayer.pushToDataLayer({ event: 'routeReady', routeReady: measurement });
      });
  }

  private measure(startedAt: number, firstLoad: boolean): Observable<RouteReadyMeasurement> {
    return this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      take(1),
      tap(() => {
        this.firstNavigationDone = true;
      }),
      switchMap(() =>
        race(
          this.dataArrived().pipe(map(at => ({ at, timedOut: false }))),
          timer(ROUTE_READY_TIMEOUT_MS).pipe(map(() => ({ at: this.now(), timedOut: true })))
        )
      ),
      map(({ at, timedOut }) => ({
        route: this.routeTemplate(),
        durationMs: Math.round(at - startedAt),
        firstLoad,
        timedOut
      })),
      takeUntil(this.pageHidden())
    );
  }

  /** Emits the time the last HTTP request finished, once no new request starts within the quiet window. */
  private dataArrived(): Observable<number> {
    return this.requestsPending$.pipe(
      filter(pending => !pending),
      map(() => this.now()),
      switchMap(idleAt =>
        timer(ROUTE_READY_QUIET_MS).pipe(
          map(() => idleAt),
          takeUntil(this.requestsPending$.pipe(filter(Boolean)))
        )
      ),
      take(1)
    );
  }

  private pageHidden(): Observable<Event> {
    return fromEvent(this.document, 'visibilitychange').pipe(filter(() => this.document.visibilityState === 'hidden'));
  }

  private routeTemplate(): string {
    const parts: string[] = [];
    let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (snapshot) {
      const path = snapshot.routeConfig?.path;
      if (path) {
        parts.push(path);
      }
      snapshot = snapshot.firstChild;
    }
    return `/${parts.join('/')}`;
  }

  private now(): number {
    return this.document.defaultView?.performance.now() ?? 0;
  }
}
